//
// Copyright © 2026 Hardcore Engineering Inc.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//

import { type AttachedDoc, type Class, type Doc, type Ref } from '@hcengineering/core'
import { type Card } from '@hcengineering/card'
import { type Employee } from '@hcengineering/contact'
import { type NotificationGroup, type NotificationType } from '@hcengineering/notification'
import { type IssueStatus, type Project } from '@hcengineering/tracker'
import { type Survey } from '@hcengineering/survey'
import { type Asset, type IntlString, plugin, type Plugin } from '@hcengineering/platform'

/**
 * Form — public intake form (Card subtype, per spec §6.5). Submissions create Issues.
 * @public
 */
export interface Form extends Card {
  survey: Ref<Survey>
  targetProject: Ref<Project>
  defaultAssignee?: Ref<Employee>
  targetStatus?: Ref<IssueStatus>
  publicLink?: string
  submissions?: number
}

/**
 * Submission — one response to a Form. AttachedDoc(Form).
 * @public
 */
export interface Submission extends AttachedDoc<Form> {
  answers: Record<string, unknown>
  submittedBy?: Ref<Employee>
  submittedFrom?: string
}

/** @public */
export const formsId = 'forms' as Plugin

export default plugin(formsId, {
  app: {
    Forms: '' as Ref<Doc>
  },
  class: {
    Form: '' as Ref<Class<Form>>,
    Submission: '' as Ref<Class<Submission>>
  },
  ids: {
    FormsNotificationGroup: '' as Ref<NotificationGroup>,
    FormSubmissionReceivedNotification: '' as Ref<NotificationType>
  },
  icon: {
    FormsApplication: '' as Asset,
    Form: '' as Asset,
    Submission: '' as Asset
  },
  string: {
    ConfigLabel: '' as IntlString,
    ConfigDescription: '' as IntlString,
    Form: '' as IntlString,
    Forms: '' as IntlString,
    Submission: '' as IntlString,
    Submissions: '' as IntlString,
    Survey: '' as IntlString,
    TargetProject: '' as IntlString,
    DefaultAssignee: '' as IntlString,
    TargetStatus: '' as IntlString,
    PublicLink: '' as IntlString,
    Answers: '' as IntlString,
    SubmittedBy: '' as IntlString,
    SubmittedFrom: '' as IntlString,
    FormApplication: '' as IntlString,
    FormSubmissionReceived: '' as IntlString,
    FormsApplication: '' as IntlString,
    AllForms: '' as IntlString,
    Templates: '' as IntlString,
    NewForm: '' as IntlString
  }
})
